import { useEffect, useRef } from 'react';
import { useConnectionState, useLocalParticipant } from '@livekit/components-react';
import { ConnectionState, Track } from 'livekit-client';

import { OralExamSessionPhase } from '@/common/api/models/oralExamSessionPhase';
import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';
import { useMeetingRoomOptional } from '@/common/components/meeting/MeetingRoomContext';
import log from '@/common/utils/logger';
import { useCarriedScreenShare } from '@/student/module/hooks/useCarriedScreenShare';

/** True when the student is in their per-booking exam room (official phase, not terminal). */
export function shouldAutoPublishMandatoryScreenShare(
  runState: Pick<OralExamRunStateResponse, 'sessionPhase' | 'state'>,
): boolean {
  if (runState.state === 'completed' || runState.state === 'aborted') {
    return false;
  }
  return runState.sessionPhase === OralExamSessionPhase.in_exam;
}

function isScreenSharePublished(localParticipant: {
  getTrackPublication: (source: Track.Source) => { track?: unknown; isMuted?: boolean } | undefined;
}): boolean {
  const pub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
  return Boolean(pub?.track && !pub.isMuted);
}

/**
 * Auto-publishes mandatory screen share in the per-booking exam room. Runs only
 * during `sessionPhase === in_exam`; pre-flight and group briefing are excluded.
 *
 * Grant persistence across the room switch: the lobby captures the display track
 * with a user gesture and retains it in {@link useCarriedScreenShare}. Here we
 * republish a fresh CLONE of that carried track (no new gesture) so the exam room
 * gets the screen back automatically. Only when nothing was carried (the source
 * ended, or the student never granted) do we fall back to a gesture-driven
 * `setScreenShareEnabled`, which may fail silently outside a gesture — the
 * integrity monitor then drives the explicit re-share UX.
 */
export function useOralExamMandatoryScreenShare(runState: OralExamRunStateResponse): void {
  const { localParticipant } = useLocalParticipant();
  const connectionState = useConnectionState();
  const meetingRoom = useMeetingRoomOptional();
  const { cloneTrack } = useCarriedScreenShare();
  const enablingRef = useRef(false);

  const shouldPublish = shouldAutoPublishMandatoryScreenShare(runState);
  const isConnected = connectionState === ConnectionState.Connected;
  const isInitialMediaSettled = meetingRoom?.initialMediaSettled ?? true;

  useEffect(() => {
    if (!localParticipant || !shouldPublish || !isConnected || !isInitialMediaSettled) {
      return undefined;
    }

    let cancelled = false;

    const ensureScreenShare = async () => {
      if (cancelled || enablingRef.current || isScreenSharePublished(localParticipant)) {
        return;
      }
      enablingRef.current = true;
      try {
        const carried = cloneTrack();
        if (carried) {
          await localParticipant.publishTrack(carried, {
            source: Track.Source.ScreenShare,
            name: 'screen',
          });
        } else {
          await localParticipant.setScreenShareEnabled(true, { audio: false });
        }
      } catch (error) {
        log.error(error instanceof Error ? error : new Error(String(error)));
      } finally {
        enablingRef.current = false;
      }
    };

    ensureScreenShare();

    const onTrackUnpublished = (publication: { source?: Track.Source }) => {
      if (cancelled || publication.source !== Track.Source.ScreenShare) {
        return;
      }
      ensureScreenShare();
    };

    localParticipant.on('localTrackUnpublished', onTrackUnpublished);

    return () => {
      cancelled = true;
      localParticipant.off('localTrackUnpublished', onTrackUnpublished);
    };
  }, [localParticipant, shouldPublish, isConnected, isInitialMediaSettled, cloneTrack]);
}
